import { CaretDownIcon } from "@radix-ui/react-icons";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import type { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { PropertyType } from "@/tailwind/tailwindUtils";
import VariantContextMenu, {
  type VariantType,
} from "../variant/VariantContextMenu";
import {
  type ComboBoxOptionType,
  type DropdownOptionType,
  type TooltipProps,
  useComboboxContainer,
} from ".";

interface ComboBoxProps {
  options: ComboBoxOptionType[];
  onOptionSelected: (option: ComboBoxOptionType, variant?: string) => void;
  input: string;
  open: boolean;
  closeCombobox: () => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  keepOpenOnSelect?: boolean;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  onOptionSelected,
  input,
  open,
  closeCombobox,
  buildArbitraryClass,
  buildOptionPreview,
  anchorRef,
  keepOpenOnSelect,
}) => {
  const { containerRef } = useComboboxContainer();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  const [firstNonNullInput, setFirstNonNullInput] = useState<string | null>(
    null,
  );
  const [inputChanged, setInputChanged] = useState(false);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef(null);

  const [comboboxStyle, setComboboxStyle] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    width?: string;
  } | null>(null);

  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);

  useEffect(() => {
    if (open && anchorRef.current && containerRef.current) {
      const inputRect = anchorRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const width = containerRect.width;
      const viewportWidth = window.innerWidth;

      const viewportHeight = window.innerHeight;

      const wouldOverflowRight = containerRect.left + width > viewportWidth;
      let left = `${containerRect.left}px`;
      if (wouldOverflowRight) {
        left = `${viewportWidth - width - 20}px`;
      }

      // Matches max-h-72
      const maxHeight = 288;
      const wouldOverflowBottom =
        inputRect.bottom + maxHeight + 20 > viewportHeight;
      let top = `${inputRect.bottom + 5}px`;
      if (wouldOverflowBottom) {
        top = `${inputRect.top - maxHeight - 20}px`;
      }

      // Initial positioning - will be refined after content renders
      setComboboxStyle({
        top,
        left,
        width: `${width}px`,
      });
    }
  }, [open, anchorRef, containerRef]);

  /**
   * Tracks the first non-null input (ie. the initial value.)
   *
   * Tracks changes to input and sets input changes.
   */
  useEffect(() => {
    if (input !== null && firstNonNullInput === null) {
      setFirstNonNullInput(input); // Store the first non-null input
    }
    if (firstNonNullInput !== null && input !== firstNonNullInput) {
      setInputChanged(true); // Mark as changed if different from first non-null input
    }
  }, [input]);

  /**
   * If there was a non-null input, and it's now empty, reset the active index.
   */
  useEffect(() => {
    if (input === "" && firstNonNullInput !== null) {
      setActiveIndex(null);
    }
  }, [input, firstNonNullInput]);

  const filteredOptions = useMemo(() => {
    const inputToFilterBy = input;

    const newOptions = options
      .map((option) => {
        let score = 0;

        if (!inputToFilterBy) {
          score = 1; // So that all items are considered when there's no searchTerm
        } else {
          const term = inputToFilterBy.toLowerCase();

          if (option.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (option.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (option.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (option.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...option, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    if (buildArbitraryClass && inputToFilterBy.length) {
      newOptions.unshift({
        ...buildArbitraryClass(inputToFilterBy),
        score: Number.MAX_SAFE_INTEGER,
      });
    }

    return newOptions;
  }, [
    input,
    open,
    options,
    buildArbitraryClass,
    inputChanged,
    firstNonNullInput,
  ]);

  const showAllOptions = firstNonNullInput !== null && !inputChanged;

  const optionsToShow = showAllOptions ? options : filteredOptions;
  const defaultIndex =
    filteredOptions.length > 1
      ? options.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind,
        )
      : -1;

  const indexOfActiveOption =
    filteredOptions.length > 1
      ? optionsToShow.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind,
        )
      : 0;

  const optionThatIsActiveRef = useRef<ComboBoxOptionType | null>(null);
  useEffect(() => {
    optionThatIsActiveRef.current =
      activeIndex !== null ? optionsToShow[activeIndex] : null;
  }, [activeIndex, optionsToShow]);

  /**
   * Reset first non-null input and input change state on close.
   */
  useEffect(() => {
    if (open && showAllOptions && activeIndex === null && defaultIndex >= 0) {
      setActiveIndex(defaultIndex);
    }

    if (!open) {
      setFirstNonNullInput(null);
      setInputChanged(false);
    }
  }, [open, showAllOptions, defaultIndex]);

  useEffect(() => {
    if (input !== null && input !== "" && firstNonNullInput !== null) {
      setActiveIndex(indexOfActiveOption);
    }
  }, [input]);

  // Recalculate position after content renders
  useEffect(() => {
    if (
      open &&
      anchorRef.current &&
      containerRef.current &&
      comboboxRef.current
    ) {
      const inputRect = anchorRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const comboboxRect = (
        comboboxRef.current as HTMLElement
      ).getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceAbove = inputRect.top;
      const spaceBelow = viewportHeight - inputRect.bottom;
      const contentHeight = comboboxRect.height;

      // Choose position based on available space and content height
      const useBottomPosition =
        spaceBelow >= contentHeight || spaceBelow > spaceAbove;

      const position = useBottomPosition
        ? { top: `${inputRect.bottom}px` }
        : { bottom: `${viewportHeight - inputRect.top}px` };

      const width = containerRect.width;
      const viewportWidth = window.innerWidth;
      const wouldOverflowRight = containerRect.left + width > viewportWidth;
      let left = `${containerRect.left}px`;
      if (wouldOverflowRight) {
        left = `${viewportWidth - width - 20}px`;
      }

      setComboboxStyle({
        ...position,
        left,
        width: `${width}px`,
      });
    }
  }, [open, filteredOptions.length]);

  const handleOptionSelected = (option: ComboBoxOptionType) => {
    onOptionSelected(option);

    if (!keepOpenOnSelect) {
      closeCombobox();
    }
  };

  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        // @ts-expect-error TS2339
        !comboboxRef.current.contains(event.target as Node)
      ) {
        // Also apply whatever the current value is. If empty, ignore
        if (input !== "" && optionThatIsActiveRef.current) {
          // handleOptionSelected(optionThatIsActiveRef.current);
        }

        closeCombobox();
        setActiveIndex(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [comboboxRef]);

  /**
   * Arrow and enter selection for combobox
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        // Move focus down
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex >= optionsToShow.length - 1
            ? 0
            : prevIndex + 1,
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        // Move focus up
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex <= 0
            ? optionsToShow.length - 1
            : prevIndex - 1,
        );
        event.preventDefault();
      } else if (event.key === "Enter" && activeIndex !== null) {
        handleOptionSelected(optionsToShow[activeIndex]);
        event.preventDefault();
      } else if (event.key === "Escape") {
        closeCombobox();
        setActiveIndex(null);
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, optionsToShow, onOptionSelected]);

  /**
   * Scroll list to match activeIndex
   */
  useEffect(() => {
    if (activeOptionRef.current) {
      const activeElement = activeOptionRef.current;
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const content = () => {
    return (
      <div className="bg-popover shadow-md rounded-md border flex flex-col">
        <div className="max-h-72 overflow-y-auto flex flex-col hide-scrollbar">
          {optionsToShow.map((cls, index) => (
            <div
              key={index}
              data-testid={`combobox-option-${cls.tailwind}`}
              className={`p-2 grow flex flex-row justify-between space-x-1 items-center cursor-pointer ${
                index === activeIndex ? "bg-accent" : "hover:bg-accent"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOptionSelected(cls);
              }}
              ref={index === activeIndex ? activeOptionRef : null}
            >
              <div className="flex flex-row space-x-2 items-center">
                {buildOptionPreview && (
                  <div className="w-4 h-4">{buildOptionPreview(cls)}</div>
                )}
                <div className="font-medium grow whitespace-nowrap line-clamp-1">
                  {cls.tailwind}
                </div>
              </div>
              <div className="flex flex-col shrink">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <div className="flex flex-col shrink text-ellipsis overflow-hidden">
                      {cls.css
                        .split("\n")
                        .map((cssLine: string, index: number) => {
                          return (
                            <div
                              key={index}
                              className="font-light text-right shrink text-secondary-n-500 line-clamp-1"
                            >
                              {cssLine}
                            </div>
                          );
                        })}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="grow p-0"
                    side="bottom"
                    avoidCollisions={false}
                  >
                    <div className="space-y-1 flex flex-col text-xs w-full overflow-hidden pt-4">
                      <h4 className="font-semibold px-4">{cls.tailwind}</h4>
                      <pre className="w-full overflow-auto px-4 pb-4">
                        <code className="css">{cls.css}</code>
                      </pre>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const comboboxContent = (
    <div
      className="fixed grow z-100002 text-xs"
      ref={comboboxRef}
      style={comboboxStyle || {}}
      data-inside-modal="true"
    >
      <div className="p-2" style={{ visibility: open ? "visible" : "hidden" }}>
        {content()}
      </div>
    </div>
  );

  if (!open || !comboboxStyle || filteredOptions.length === 0) {
    return null;
  }

  if (!portalRoot) {
    return comboboxContent;
  }

  return ReactDOM.createPortal(comboboxContent, portalRoot);
};

interface VariantIndicatorProps {
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
    valueByVariant: Record<
      string,
      {
        value: string;
        displayValue: string | null;
        active: boolean;
      }
    >;
  };
}

const VariantIndicator = ({ propertyState }: VariantIndicatorProps) => {
  return (
    <div
      className={cn(
        "absolute right-[-3px] top-[-3px] bg-background-CTA size-[6px] rounded-full",

        // If there are variants and none are active, show white dot
        Object.keys(propertyState.valueByVariant).length >= 1 &&
          Object.keys(propertyState.valueByVariant).filter(
            (variant) => propertyState.valueByVariant[variant].active,
          ).length === 0 &&
          "bg-white",
        // If there are no variants and the style is not set, show no dot
        !propertyState.isSet &&
          Object.keys(propertyState.valueByVariant).length === 0 &&
          "hidden",
        // If the only variant is the default one, show no dot
        Object.keys(propertyState.valueByVariant).length === 1 &&
          propertyState.valueByVariant["default"] &&
          "hidden",
      )}
    />
  );
};

interface DraggableInputProps {
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
    valueByVariant: Record<
      string,
      {
        value: string;
        displayValue: string | null;
        active: boolean;
      }
    >;
  };
  labelContent?: ReactNode;
  comboboxOptions?: ComboBoxOptionType[];
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  onComboboxSelected?: (option: ComboBoxOptionType, variant?: string) => void;
  onSliderValueUpdate?: (option: ComboBoxOptionType, variant?: string) => void; // Must be supplied with build arbitrary class fn
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  wrap?: boolean;
  fallback?: { number: number; unit: string }; // Used if the value cannot be parsed
  draggable?: boolean;
  onClear: (variant?: string) => void;
  className?: string;
  placeholder?: string;
  showCombox?: boolean;
  hideComboxOptions?: (value: boolean) => void;
  inputClassName?: string;
  keepOpenOnSelect?: boolean;
  disableContextMenu?: boolean;
  fixedOptionsOnly?: boolean;
  "data-testid"?: string;
}

const VariantDraggableInput: React.FC<DraggableInputProps & TooltipProps> = ({
  showCombox,
  propertyState,
  labelContent = null,
  dropdownOptions = [],
  onDropdownSelected,
  comboboxOptions,
  onComboboxSelected,
  onSliderValueUpdate,
  onClear,
  buildArbitraryClass,
  buildOptionPreview,
  onInputFocus,
  disabled = false,
  min,
  max,
  step,
  wrap = false,
  draggable = true,
  tooltipContent = null,
  fallback,
  className,
  placeholder,
  hideComboxOptions,
  inputClassName,
  keepOpenOnSelect,
  disableContextMenu = false,
  fixedOptionsOnly = false,
  "data-testid": dataTestId,
}) => {
  const [snapshot, setSnapshot] = useState(
    propertyState.cssValue || propertyState.propertyValue,
  );
  const [startVal, setStartVal] = useState<null | number>(null);
  const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false);
  const [comboboxOpen, setComboboxOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState(false);
  const [selectedVariantForCombobox, setSelectedVariantForCombobox] = useState<
    string | undefined
  >(undefined);
  const totalDragRef = useRef<number>(0);
  const latestValueRef = useRef<string | null>(
    propertyState.cssValue || propertyState.propertyValue,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const draggableInputRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState(propertyState.displayValue);
  const lastCommittedValueRef = useRef<string>("");

  const ignoreBlurRef = useRef<boolean>(false);

  const activeVariant = useMemo(() => {
    return Object.keys(propertyState.valueByVariant).find(
      (variant) => propertyState.valueByVariant[variant].active,
    );
  }, [propertyState.valueByVariant]);

  const effectiveVariant = selectedVariantForCombobox || activeVariant;

  // Extract the number and unit from the string value.
  const extractNumberAndUnit = (val: string) => {
    let stringToExtractFrom = val;
    if (propertyState.propertyType === PropertyType.BLUR) {
      // Extract the value inside the blur() function
      const match = val.match(/blur\(([^)]+)\)/);
      if (!match?.length) {
        return null;
      }

      stringToExtractFrom = match[1];
    }

    const match = stringToExtractFrom.match(/^(-?\d+(\.\d+)?)([a-zA-Z%]*)$/);
    if (match) {
      return {
        number: parseFloat(match[1]),
        unit: match[3],
      };
    }

    if (fallback) {
      return fallback;
    }

    return null;
  };

  const formatNewValue = (updatedNumber: number, updatedUnit: string) => {
    if (propertyState.propertyType === PropertyType.BLUR) {
      return `blur(${updatedNumber}${updatedUnit})`;
    }
    return `${updatedNumber}${updatedUnit}`;
  };

  useEffect(() => {
    if (!focused) {
      setInputValue(propertyState.displayValue);
    }
  }, [propertyState.displayValue, focused]);

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = ev.target.value;
    setInputValue(currentValue);

    if (comboboxOptions && comboboxOptions.length > 0) {
      setComboboxOpen(true);
      setContextMenuVisible(false);
    }
  };

  const onStart = useCallback(
    (event: React.MouseEvent) => {
      const value = propertyState.cssValue || propertyState.propertyValue;

      setStartVal(event.clientX);
      setSnapshot(value);
    },
    [propertyState.cssValue, propertyState.propertyValue],
  );

  const handleComboboxOptionClick = (
    option: ComboBoxOptionType,
    variant?: string,
  ) => {
    const finalVariant = variant || effectiveVariant;
    if (onComboboxSelected) {
      onComboboxSelected(option, finalVariant);
    }

    // Update the input to show the selected value
    setInputValue(option.tailwind);
    setComboboxOpen(false);
    hideComboxOptions?.(false);

    if (inputRef.current) {
      ignoreBlurRef.current = true;
      inputRef.current.blur();
    }
  };

  const processInputValue = (value: string) => {
    // If it's just a number without units, append "px"
    if (/^\d+(\.\d+)?$/.test(value.trim())) {
      return `${value}px`;
    }
    return value;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    setComboboxOpen(false);
    hideComboxOptions?.(false);

    if (inputValue === "") {
      onClear(effectiveVariant);
    } else if (inputValue && !fixedOptionsOnly) {
      const processedValue = processInputValue(inputValue);

      if (onComboboxSelected) {
        if (buildArbitraryClass) {
          const option = buildArbitraryClass(processedValue);
          onComboboxSelected(option, effectiveVariant);
        } else {
          // If no buildArbitraryClass, pass the raw value as a simple option
          const option = { tailwind: processedValue, css: processedValue };
          onComboboxSelected(option, effectiveVariant);
        }
      }

      // Track the committed value to prevent duplicate commits
      lastCommittedValueRef.current = processedValue;
    }

    if (inputRef.current) {
      ignoreBlurRef.current = true;
      inputRef.current.blur();
    }
  };

  /**
   * Draggable input effect
   */
  useEffect(() => {
    const onUpdate = (event: MouseEvent) => {
      if (startVal !== null) {
        const diff = event.clientX - startVal;
        totalDragRef.current += Math.abs(diff);

        const extracted = extractNumberAndUnit(snapshot);
        if (extracted) {
          let updatedNumber = extracted.number + diff * (step || 1);

          // Check if there is a min value and if the current value is below it
          if (typeof min === "number" && updatedNumber < min) {
            updatedNumber = wrap ? max || updatedNumber : min;
          }

          // Check if there is a max value and if the current value is above it
          if (typeof max === "number" && updatedNumber > max) {
            updatedNumber = wrap ? min || updatedNumber : max;
          }

          // Update the input display while dragging
          setInputValue(formatNewValue(updatedNumber, extracted.unit));

          const updatedValue = `${updatedNumber}${extracted.unit}`;
          latestValueRef.current = updatedValue;

          // Send instant update for visual feedback while dragging
          if (onSliderValueUpdate && buildArbitraryClass) {
            // Not formatted - the parent formats it for the property type
            onSliderValueUpdate(
              buildArbitraryClass(updatedValue),
              effectiveVariant,
            );
          }
        }
      }
    };

    const onEnd = () => {
      if (totalDragRef.current > 5) {
        if (
          buildArbitraryClass &&
          onComboboxSelected &&
          latestValueRef.current
        ) {
          onComboboxSelected(
            buildArbitraryClass(latestValueRef.current),
            effectiveVariant,
          ); // Using value, as it's the final value after dragging
        }
      }

      totalDragRef.current = 0;
      latestValueRef.current = null;
      setStartVal(null);
    };

    document.addEventListener("mousemove", onUpdate);
    document.addEventListener("mouseup", onEnd);
    return () => {
      document.removeEventListener("mousemove", onUpdate);
      document.removeEventListener("mouseup", onEnd);
    };
  }, [startVal, snapshot, effectiveVariant]);

  /**
   * Track changes to disabled to ensure an unusable input isn't in focus.
   */
  useEffect(() => {
    if (disabled) {
      setFocused(false);
      setComboboxOpen(false);
      setContextMenuVisible(false);
      hideComboxOptions?.(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!focused) {
      setInputValue(propertyState.displayValue);
    }
  }, [propertyState.displayValue, focused]);

  return (
    <div
      data-testid={dataTestId}
      data-tooltip-id="tooltip"
      data-tooltip-content={tooltipContent}
      data-tooltip-place="bottom"
      data-tooltip-offset={5}
      data-tooltip-hidden={comboboxOpen || contextMenuVisible}
      ref={draggableInputRef}
    >
      <div
        className={cn(
          "flex flex-row items-center px-1 border border-transparent hover:border-secondary-n-600 relative bg-secondary-n-950 rounded-md h-7 min-w-18",
          focused && "border-primary-0!",
          className,
        )}
        onContextMenu={(e) => {
          // Always prevent browser context menu
          e.preventDefault();
          e.stopPropagation();

          if (disableContextMenu) {
            return false;
          }

          if (dropdownOptions && dropdownOptions?.length > 0) {
            setContextMenuVisible(true);
            setComboboxOpen(false);
            hideComboxOptions?.(false);
          }

          return false;
        }}
        onMouseDown={(event) => {
          if (event.button === 2) {
            // Right click
            event.preventDefault();
            event.stopPropagation();
            setContextMenuVisible(true);
            setComboboxOpen(false);
            hideComboxOptions?.(false);
          }
        }}
      >
        {/* Dropdown content */}
        {labelContent && (
          <div
            onMouseDown={(event: React.MouseEvent) => {
              if (draggable) {
                onStart(event);
              }
            }}
            className={`py-1 pl-1.5 pr-2 text-secondary-n-600  ${
              draggable ? "cursor-ew-resize" : ""
            } select-none min-w-2`}
          >
            {labelContent}
          </div>
        )}
        <input
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          onFocus={(event) => {
            setFocused(true);
            event.target.select();
            // Only open combobox on focus, not the context menu
            if (comboboxOptions && comboboxOptions.length > 0) {
              setComboboxOpen(true);
              setContextMenuVisible(false);
            }

            if (onInputFocus) {
              onInputFocus(event);
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setFocused(false);
            setComboboxOpen(false);
            setContextMenuVisible(false);
            hideComboxOptions?.(false);

            if (ignoreBlurRef.current) {
              ignoreBlurRef.current = false;
              return;
            }

            if (inputValue && inputValue !== propertyState.displayValue) {
              const processedValue = processInputValue(inputValue);

              if (processedValue === lastCommittedValueRef.current) {
                return;
              }

              if (onComboboxSelected) {
                if (buildArbitraryClass) {
                  const option = buildArbitraryClass(processedValue);
                  onComboboxSelected(option, effectiveVariant);
                } else {
                  const option = {
                    tailwind: processedValue,
                    css: processedValue,
                  };
                  onComboboxSelected(option, effectiveVariant);
                }
              }

              lastCommittedValueRef.current = processedValue;
            } else if (!inputValue || inputValue === "") {
              // If empty, clear the value
              onClear(effectiveVariant);
            }
          }}
          className={cn(
            "text-xs px-1 border-none outline-hidden w-full font-medium bg-transparent placeholder:text-secondary-n-600",
            {
              "text-secondary-n-400": disabled || !propertyState.isSet,
              "text-text-primary": !disabled && propertyState.isSet,
            },
            inputClassName,
          )}
          ref={inputRef}
        />

        <div
          onClick={() => {
            setContextMenuVisible(true);
            setComboboxOpen(false);
            hideComboxOptions?.(false);
          }}
        >
          <CaretDownIcon className="mr-1" />
        </div>
        <VariantIndicator propertyState={propertyState} />
      </div>
      {((comboboxOpen && comboboxOptions) || showCombox) && (
        <ComboBox
          input={inputValue}
          options={comboboxOptions!}
          open={comboboxOpen || showCombox!}
          closeCombobox={() => {
            setComboboxOpen(false);
            hideComboxOptions?.(false);
            setSelectedVariantForCombobox(undefined);
          }}
          onOptionSelected={(option: ComboBoxOptionType) =>
            handleComboboxOptionClick(option, selectedVariantForCombobox)
          }
          buildOptionPreview={buildOptionPreview}
          buildArbitraryClass={buildArbitraryClass}
          anchorRef={draggableInputRef as React.RefObject<HTMLElement>}
          keepOpenOnSelect={keepOpenOnSelect}
        />
      )}

      <VariantContextMenu
        isOpen={contextMenuVisible}
        onClose={() => setContextMenuVisible(false)}
        anchorRef={draggableInputRef as React.RefObject<HTMLElement>}
        valueByVariant={propertyState.valueByVariant}
        onSelectVariant={(variant: VariantType) => {
          // Set the variant that the combobox should apply values to
          setSelectedVariantForCombobox(variant);
          setComboboxOpen(true);
          setContextMenuVisible(false);
        }}
        onClearVariant={(variant?: VariantType) => {
          onClear(variant);
          setContextMenuVisible(false);
          setComboboxOpen(false);
          hideComboxOptions?.(false);
        }}
        dropdownOptions={dropdownOptions}
        onDropdownSelected={(option) => {
          if (option.value === "clear-style") {
            onClear(effectiveVariant);
          } else if (onDropdownSelected) {
            onDropdownSelected(option);
          }
          setContextMenuVisible(false);
          setComboboxOpen(false);
          hideComboxOptions?.(false);
        }}
      />
    </div>
  );
};

export { VariantDraggableInput };
